import { getOptionalLocaleFromPathname } from '@susu/locale-utils'

export type PageType =
  | 'account'
  | 'black_tie_configurator'
  | 'cart'
  | 'category_landing'
  | 'category'
  | 'checkout'
  | 'configurator'
  | 'custommade'
  | 'error'
  | 'giftcard'
  | 'home'
  | 'journal'
  | 'landing'
  | 'login'
  | 'lookbuilder'
  | 'other'
  | 'preference_center'
  | 'product'
  | 'return'
  | 'search'
  | 'store'
  | 'support'
  | 'wishlist'
  | 'booking_tool'

// Ordered: first match wins, so specific prefixes (e.g. /men/black-tie-package)
// must stay ahead of broader ones (e.g. the /men/ category prefix).
const pageTypesByPrefix: Array<[PageType, string[]]> = [
  ['error', ['/error', '/404', '/500']],
  ['support', ['/support', '/help', '/contact']],
  [
    'account',
    ['/account', '/profile', '/my-account', '/order-details', '/orders'],
  ],
  ['login', ['/login', '/signin', '/register', '/signup']],
  ['wishlist', ['/wishlist', '/favorites']],
  ['cart', ['/cart', '/bag']],
  ['checkout', ['/checkout', '/payment', '/billing']],
  ['giftcard', ['/giftcard', '/gift-card', '/gift']],
  ['store', ['/stores', '/store/', '/find-store']],
  ['journal', ['/journal', '/blog', '/content']],
  ['black_tie_configurator', ['/men/black-tie-package', '/black-tie-package']],
  ['custommade', ['/custom-made', '/custommade', '/configurator']],
  ['lookbuilder', ['/lookbuilder', '/look-builder', '/outfit-builder']],
  ['search', ['/search']],
  ['configurator', ['/socks-configurator']],
  ['preference_center', ['/unsubscribe']],
]

// Content pages served as `<name>.html` directly under the locale.
const contentPages: Array<[PageType, RegExp]> = [
  [
    'support',
    /^\/(delivery-time-cost|customer-service|faq(-[\w-]+)?|privacy-statement|secure-payments|terms-conditions|all-phone-numbers|cookie-policy|return-refund-and-exchange-[\w-]+|impressum-[\w-]+|corporate-service|sizing-size-passports|our-commitment-accessibility)\.html$/,
  ],
  [
    'landing',
    /^\/(xmas-[\w-]+|overview|csr-landing|about-us|corporate-social-responsibility|subscription-landing|the-adult-man|tailoring|about-career)\.html$/,
  ],
]

const skuPatterns = [
  /\/[A-Z0-9]+\.html$/, // SKU pattern like /H7404.html
  /\/[A-Z0-9]+-[A-Z0-9]+\.html$/, // SKU with variant like /C7334-S.html
  /\/[a-z0-9-]+\/[A-Z0-9]+\.html$/, // Product path with SKU
]

const categoryLandingPrefixes = [
  '/discover-all-suits',
  '/shop-tuxedo-collection',
  '/shop-selection',
]

const categoryPrefixes = [
  '/new-arrivals',
  '/best-sellers',
  '/sale',
  '/clearance',
  '/men/',
  '/women/',
  '/accessories/',
  '/suits/',
  '/shirts/',
  '/trousers/',
  '/shoes/',
  '/coats/',
  '/jackets/',
  '/knitwear/',
  '/ties/',
  '/belts/',
  '/socks/',
  '/handkerchiefs/',
  '/cufflinks/',
]

export const getPageType = (): PageType => {
  // Guard statement for SSR/backend/test environments where window is not defined
  if (typeof window === 'undefined') {
    return 'home'
  }

  // Only a real locale prefix is stripped: an old SFCC path such as `/de` or
  // `/en_us/home` has none, and stripping six characters made it `home`.
  const fullPathname = window.location?.pathname || ''
  const locale = getOptionalLocaleFromPathname(fullPathname)
  const pathname = locale ? fullPathname.slice(locale.length + 1) : fullPathname

  // Appointment booking pages (subdomain)
  if (window.location.hostname.includes('appointment.suitsupply.com')) {
    return 'booking_tool'
  }

  const contentPage = contentPages.find(([, pattern]) => pattern.test(pathname))
  if (contentPage) {
    return contentPage[0]
  }

  const prefixed = pageTypesByPrefix.find(([, prefixes]) =>
    prefixes.some(prefix => pathname.startsWith(prefix)),
  )
  if (prefixed) {
    return prefixed[0]
  }

  if (skuPatterns.some(pattern => pattern.test(pathname))) {
    return 'product'
  }

  if (categoryLandingPrefixes.some(prefix => pathname.startsWith(prefix))) {
    return 'category_landing'
  }

  if (categoryPrefixes.some(prefix => pathname.startsWith(prefix))) {
    return 'category'
  }

  if (pathname.startsWith('/returns')) {
    return 'return'
  }

  // `home` is only the locale root (`/en-nl/`); an unknown path is not a home
  // page view.
  return pathname === '' || pathname === '/' ? 'home' : 'other'
}
